import React, { useEffect } from "react";
import {
  X,
  Clock,
  Calendar,
  Code,
  Check,
  Loader,
  FileText,
  Shield,
  Info,
} from "lucide-react";
import { normalizePermissionFeatureName } from "../../../utils/permissionNormalization";
import { getWorkflowApprovalLabel, getCurrentStepLabel } from "../Constant/EmployeeActions.config";

const PendingApprovalsModal = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  pendingActions,
  pendingWorkflows,
  isSubmitting,
  permissions,
  isAdminView,
  isAdminRole,
  ACTION_TYPES,
  profile,
  workflowDefinitions,
  highlightActionId = null,
  onApprove,
  onReject,
  onClose,
}) => {
  // Notification deep-link: scroll the highlighted change into view.
  useEffect(() => {
    if (!highlightActionId) return;
    const timer = setTimeout(() => {
      document.getElementById(`pending-action-${highlightActionId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightActionId]);

  const normalizeApproverValue = (value) =>
    String(value || "").trim().toLowerCase();

  const currentUserCandidates = [
    profile?.infinityCode,
    profile?.employeeId,
    profile?.userId,
    profile?.code,
    localStorage.getItem("infinityCode"),
    localStorage.getItem("userInfinityCode"),
    localStorage.getItem("employeeCode"),
    sessionStorage.getItem("infinityCode"),
  ].filter(Boolean);
  const currentUserNormalized = currentUserCandidates.map(normalizeApproverValue);

  const currentAdminLevelIds = [
    profile?.adminLevelId,
    profile?.adminLevel?.id,
    profile?.admin_level_id,
    (() => {
      try {
        const raw = localStorage.getItem("adminLevel");
        if (raw) {
          const obj = JSON.parse(raw);
          return obj?.adminLevelId || obj?.admin_level_id || obj?.id || obj?.adminLevel?.id || null;
        }
      } catch {
        return null;
      }
    })(),
    (() => {
      try {
        const userStr =
          localStorage.getItem("user") || sessionStorage.getItem("user");
        const obj = userStr ? JSON.parse(userStr) : null;
        return obj?.adminLevelId || obj?.admin_level_id || obj?.adminLevel?.id || null;
      } catch {
        return null;
      }
    })(),
  ].filter(Boolean).map((value) => String(value).trim());

  const checkIsApprover = (action) => {
    const wfStepsForAction =
      pendingWorkflows?.[String(action.id)] ||
      pendingWorkflows?.[action.id] ||
      pendingWorkflows?.[String(action.workflowInstanceId)] ||
      pendingWorkflows?.[action.workflowInstanceId] ||
      [];

    const employeeManagerCodes = [
      action?.employee?.firstLineManagerInfinityCode,
      action?.employee?.secondLineManagerInfinityCode,
      action?.employee?.firstLineManager?.infinityCode,
      action?.employee?.secondLineManager?.infinityCode,
      action?.employee?.managerInfinityCode,
      action?.employee?.directManager,
      action?.employee?.lineManager,
    ].filter(Boolean).map(normalizeApproverValue);

    return Array.isArray(wfStepsForAction)
      ? wfStepsForAction.some((step) => {
        const stepStatus = String(step?.status || "").trim().toLowerCase();
        if (stepStatus !== "pending") return false;

        // Alternate approvers act exactly like the main assignee of the
        // equivalent type — any one of them showing up here means this user
        // can approve/reject the step. Each alternate is now a FULL
        // assignee-type config resolved server-side to { assigneeType,
        // assignedToInfinityCode, assignedToAdminLevelId, assignedToAnyAdmin }
        // (mirroring the step's own four scalar columns), not a bare code, so
        // pull the relevant field out of each rather than spreading raw
        // entries into a code list. alternateAssignees can arrive as an
        // array, null, or (defensively) a JSON string from the API.
        const resolvedAlternates = (() => {
          const raw = step?.alternateAssignees;
          if (Array.isArray(raw)) return raw;
          if (typeof raw === "string" && raw.trim()) {
            try {
              const parsed = JSON.parse(raw);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        })().filter((entry) => entry && typeof entry === "object");

        const alternateCodes = resolvedAlternates.map((alt) => alt.assignedToInfinityCode).filter(Boolean);
        const alternateAdminLevelIds = resolvedAlternates.map((alt) => alt.assignedToAdminLevelId).filter(Boolean);

        const assignedCodes = [
          step?.assignedToInfinityCode,
          step?.explicitAssigneeInfinityCode,
          step?.assignedTo,
          step?.assignedToId,
          step?.assignedToInfinity,
          step?.assigneeInfinityCode,
          step?.assignee?.infinityCode,
          step?.assignee?.code,
          ...alternateCodes,
        ].filter(Boolean).map(normalizeApproverValue);

        const isUserAssignee = assignedCodes.some((code) =>
          currentUserNormalized.includes(code)
        );
        const isAdminAssignee = Boolean(
          currentAdminLevelIds.length &&
          [step?.assignedToAdminLevelId, ...alternateAdminLevelIds].some(
            (levelId) => levelId && currentAdminLevelIds.includes(String(levelId).trim())
          )
        );
        const isEmployeeManager = employeeManagerCodes.some((code) =>
          currentUserNormalized.includes(code)
        );

        return isUserAssignee || isAdminAssignee || isEmployeeManager;
      })
      : false;
  };

  // Filter actions: For non-admins (!isAdminRole && !isAdminView), only show actions where it is currently the user's turn (isApprover === true).
  // For admins, show all actions.
  const visiblePendingActions = pendingActions.filter((action) => {
    if (isAdminRole || isAdminView) return true;
    return checkIsApprover(action);
  });

  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: "min(95vw, 1200px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              alignItems: "center",
            }}
          >
            <div
              style={{
                ...styles.modalIconWrapper,
                background: "linear-gradient(135deg, #ec4899, #db2777)",
              }}
            >
              <Clock size={26} color="white" />
            </div>
            <div>
              <h2 style={styles.modalTitle}>{t.pendingApprovalsTitle}</h2>
              <p
                style={{
                  fontSize: "clamp(12px, 1.7vw, 14px)",
                  color:
                    theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                  margin: "4px 0 0 0",
                  fontWeight: "600",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {visiblePendingActions.length} {t.actionsAwaiting}
              </p>
            </div>
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            className="enhanced-button-hover"
          >
            <X size={24} />
          </button>
        </div>

        {isSubmitting && (
          <div style={styles.loadingOverlay}>
            <div style={{ textAlign: "center" }}>
              <Loader size={48} style={styles.loadingSpinner} color="#2e7cba" />
              <p
                style={{
                  marginTop: "16px",
                  fontWeight: "600",
                  color:
                    theme === "dark" ? "rgba(255,255,255,0.8)" : "#64748b",
                }}
              >
                {t.processing}
              </p>
            </div>
          </div>
        )}

        <div style={styles.tableWrapper}>
          {visiblePendingActions.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t.requestedDate}</th>
                  <th style={styles.th}>{t.employee}</th>
                  <th style={styles.th}>{t.actionType}</th>
                  <th style={styles.th}>{t.effectiveDate}</th>
                  <th style={styles.th}>{t.details}</th>
                  <th style={{ ...styles.th, width: "220px" }}>{t.approvalLevel}</th>
                  <th style={styles.th}>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {visiblePendingActions.map((action) => {
                  const actionConfig = ACTION_TYPES.find(
                    (a) => a.id === action.actionType
                  );
                  const ActionIcon = actionConfig?.icon || FileText;

                  const isHighlighted = Number(highlightActionId) === Number(action.id);

                  const wfStepsForAction =
                    pendingWorkflows?.[String(action.id)] ||
                    pendingWorkflows?.[action.id] ||
                    pendingWorkflows?.[String(action.workflowInstanceId)] ||
                    pendingWorkflows?.[action.workflowInstanceId] ||
                    [];

                  const isApprover = checkIsApprover(action);

                  const actionDepartment =
                    action?.employee?.department?.name ||
                    action?.oldDepartment ||
                    action?.newDepartment ||
                    null;

                  const currentStepLabel = actionConfig
                    ? getCurrentStepLabel(action, wfStepsForAction, language, t)
                    : null;
                  const fullChainLabel = actionConfig
                    ? getWorkflowApprovalLabel(
                      actionConfig.id,
                      workflowDefinitions,
                      language,
                      t,
                      actionConfig.approvalLevelKey,
                      actionDepartment
                    )
                    : t.notAvailable;

                  const displayStepLabel = currentStepLabel || fullChainLabel;
                  const hasFullChainDetail =
                    Boolean(fullChainLabel) && fullChainLabel !== displayStepLabel;
                  const fullChainPrefix =
                    t.fullChainLabel ||
                    (language === "ar" ? "سلسلة الموافقات الكاملة" : "Full Chain");

                  return (
                    <tr
                      key={action.id}
                      id={`pending-action-${action.id}`}
                      className="table-row-hover"
                      style={isHighlighted ? { outline: "2px solid #3b82f6", outlineOffset: "-2px" } : undefined}
                    >
                      <td style={styles.td}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: isRTL ? "row" : "row",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Calendar size={16} />
                          {new Date(
                            action.createdAt || action.requestedDate
                          ).toLocaleDateString(
                            language === "ar" ? "ar-EG" : "en-US"
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.employeeInfo}>
                          <div style={styles.employeeName}>
                            {language === "ar"
                              ? action.employeeArabicName ||
                              action.employeeName ||
                              action.newData?.employeeName ||
                              t.notAvailable
                              : action.employeeName ||
                              action.employeeArabicName ||
                              action.newData?.employeeName ||
                              t.notAvailable}
                          </div>
                          <div style={styles.employeeDetail}>
                            <Code size={13} />
                            {action.clientCode ||
                              action.employee?.clientCode ||
                              action.infinityCode ||
                              action.employeeId ||
                              t.notAvailable}
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            ...styles.badge,
                            background:
                              actionConfig?.bgColor ||
                              "rgba(100, 116, 139, 0.15)",
                            border: `1px solid ${actionConfig?.borderColor ||
                              "rgba(100, 116, 139, 0.4)"
                              }`,
                            color: theme === "dark" ? "#ffffff" : "#0f172a",
                          }}
                        >
                          <ActionIcon size={14} />
                          {actionConfig
                            ? t[actionConfig.labelKey]
                            : action.actionType}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: isRTL ? "row" : "row",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Calendar size={16} />
                          {action.effectiveDate
                            ? new Date(
                              action.effectiveDate
                            ).toLocaleDateString(
                              language === "ar" ? "ar-EG" : "en-US"
                            )
                            : t.notAvailable}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            fontSize: "clamp(11px, 1.6vw, 13px)",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.65)"
                                : "#64748b",
                            maxWidth: "250px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textAlign: isRTL ? "right" : "left",
                          }}
                          title={action.reason}
                        >
                          {action.reason || t.noDetailsProvided}
                        </div>
                        {action.oldData && action.newData && (
                          <div
                            style={{
                              fontSize: "clamp(10px, 1.5vw, 11px)",
                              color:
                                theme === "dark"
                                  ? "rgba(255,255,255,0.5)"
                                  : "#94a3b8",
                              marginTop: "4px",
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {action.oldData.position &&
                              action.newData.position &&
                              `${action.oldData.position} ${isRTL ? "←" : "→"
                              } ${action.newData.position}`}
                            {action.oldData.salary &&
                              action.newData.salary &&
                              ` | ${t.egp} ${action.oldData.salary?.toLocaleString()} ${isRTL ? "←" : "→"
                              } ${action.newData.salary?.toLocaleString()}`}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            ...styles.approvalCard,
                            background: isApprover
                              ? "rgba(16, 185, 129, 0.12)"
                              : "rgba(139, 92, 246, 0.15)",
                            border: `1px solid ${isApprover
                                ? "rgba(16, 185, 129, 0.4)"
                                : "rgba(139, 92, 246, 0.4)"
                              }`,
                            color:
                              theme === "dark"
                                ? isApprover
                                  ? "#6ee7b7"
                                  : "#c4b5fd"
                                : isApprover
                                  ? "#047857"
                                  : "#7c3aed",
                          }}
                          title={
                            hasFullChainDetail
                              ? `${fullChainPrefix}: ${fullChainLabel}`
                              : undefined
                          }
                        >
                          <div style={styles.approvalStepRow}>
                            <Shield size={14} style={{ flexShrink: 0 }} />
                            <span style={styles.approvalStepLabel}>
                              {displayStepLabel}
                            </span>
                            {hasFullChainDetail && (
                              <Info
                                size={12}
                                style={{ flexShrink: 0, opacity: 0.6 }}
                              />
                            )}
                          </div>

                          {isApprover && (
                            <span
                              style={{
                                ...styles.approvalTag,
                                color: "#059669",
                                background: "rgba(16, 185, 129, 0.2)",
                              }}
                            >
                              {language === "ar" ? "دورك للموافقة" : "Action Required (Your Step)"}
                            </span>
                          )}

                          {!isApprover && isAdminRole && (
                            <span
                              style={{
                                ...styles.approvalTag,
                                color: "#d97706",
                                background: "rgba(245, 158, 11, 0.2)",
                              }}
                            >
                              {language === "ar" ? "تجاوز المشرف (خطوة معلقة)" : "Admin Override (Step Pending)"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: isRTL ? "row" : "row",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {(() => {
                            if (!isApprover && !isAdminRole) return null;

                            const grantedFeatures = Array.isArray(
                              permissions?.features?.changesPage
                            )
                              ? permissions.features.changesPage.map((f) =>
                                String(f || "").trim().toLowerCase()
                              )
                              : [];
                            const canApprove =
                              !isAdminView ||
                              isAdminRole ||
                              grantedFeatures.includes("approveactions");
                            const canReject =
                              !isAdminView ||
                              isAdminRole ||
                              grantedFeatures.includes("rejectactions");

                            return (
                              <>
                                {canApprove && (
                                  <button
                                    style={{
                                      ...styles.actionButton,
                                      background:
                                        "linear-gradient(135deg, #10b981, #059669)",
                                      padding:
                                        "clamp(8px, 1.3vw, 10px) clamp(14px, 2vw, 16px)",
                                      fontSize: "clamp(11px, 1.6vw, 12px)",
                                    }}
                                    onClick={() => onApprove(action)}
                                    className="action-btn-hover"
                                    disabled={isSubmitting}
                                  >
                                    <Check size={14} /> {t.approve}
                                  </button>
                                )}
                                {canReject && (
                                  <button
                                    style={{
                                      ...styles.actionButton,
                                      background:
                                        "linear-gradient(135deg, #ef4444, #dc2626)",
                                      padding:
                                        "clamp(8px, 1.3vw, 10px) clamp(14px, 2vw, 16px)",
                                      fontSize: "clamp(11px, 1.6vw, 12px)",
                                    }}
                                    onClick={() => onReject(action)}
                                    className="action-btn-hover"
                                    disabled={isSubmitting}
                                  >
                                    <X size={14} /> {t.reject}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>
                <Clock size={80} />
              </div>
              <h3 style={styles.emptyStateTitle}>{t.noPendingApprovals}</h3>
              <p style={styles.emptyStateText}>{t.allActionsReviewed}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalsModal;